import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useAsyncAction } from "shared/lib/useAsyncAction"
import { useEvent } from "shared/lib/useEvent"
import { TeamMember } from "shared/types/__generated__/graphql"
import { Button } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamMemberListProps } from "../TeamMemberList"
import "../styles/_team-member-invitation-status.scss"

interface TeamMemberInvitationStatusProps {
  teamMember: TeamMember
  onResendInvitation?: TeamMemberListProps["onResendInvitation"]
}

export const TeamMemberInvitationStatus = React.memo(
  ({ teamMember, onResendInvitation }: TeamMemberInvitationStatusProps) => {
    const { t } = useTranslation()
    const { execute, isLoading } = useAsyncAction(onResendInvitation)

    const handleResendInvite = useEvent(() => {
      execute?.(teamMember.id)
    })

    return (
      <div
        className={classNames("team-member-invitation-status", {
          "team-member-invitation-status--accepted":
            teamMember.invitationAccepted,
          "team-member-invitation-status--pending":
            !teamMember.invitationAccepted
        })}
      >
        {teamMember.invitationAccepted ? (
          <div className="team-member-invitation-status__text">
            {t("Team.Invitation.Status.Outgoing.Accepted")}
          </div>
        ) : (
          <>
            <Icon
              name={IconName.CLOSE_CIRCLE_SOLID}
              className="team-member-invitation-status__icon"
            />
            <span className="team-member-invitation-status__text">
              {t("Team.Invitation.Status.Outgoing.Pending")}
            </span>
            {onResendInvitation ? (
              <span className="team-member-invitation-status__resend-invitation">
                <Button
                  disabled={isLoading}
                  variant="link"
                  onClick={handleResendInvite}
                  label={`(${t("Team.Invitation.Resend")})`}
                />
              </span>
            ) : null}
          </>
        )}
      </div>
    )
  }
)
